import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Connecting your account...');
  const [subMessage, setSubMessage] = useState('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      console.log('=== OAuth Callback Debug ===');
      console.log('Code:', code ? code.substring(0, 30) + '...' : 'null');
      console.log('State:', state);
      console.log('Error:', error);
      console.log('Token from context:', token ? token.substring(0, 30) + '...' : 'null');
      console.log('Token from localStorage:', localStorage.getItem('token') ? localStorage.getItem('token').substring(0, 30) + '...' : 'null');

      // Handle OAuth errors from Meta
      if (error) {
        console.log('OAuth error from Meta:', error, errorDescription);
        setStatus('error');
        setMessage('Connection cancelled');
        setSubMessage(errorDescription || error);
        setTimeout(() => {
          navigate('/channels?oauth_error=' + error);
        }, 2000);
        return;
      }

      if (!code) {
        console.log('No code received');
        setStatus('error');
        setMessage('No authorization code received');
        setTimeout(() => {
          navigate('/channels?oauth_error=no_code');
        }, 2000);
        return;
      }

      // Determine channel type from state (messenger:userId or just userId for instagram)
      const channelType = state && state.startsWith('messenger:') ? 'messenger' : 'instagram';
      const authToken = token || localStorage.getItem('token');
      
      console.log('Channel type:', channelType);
      console.log('Auth token to use:', authToken ? authToken.substring(0, 30) + '...' : 'null');
      console.log('API URL:', API_URL);

      if (!authToken) {
        console.error('No auth token available!');
        setStatus('error');
        setMessage('Authentication expired');
        setSubMessage('Please log in again and retry.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }

      try {
        console.log('Calling backend:', API_URL + '/api/channels/' + channelType + '/callback');
        
        // Exchange code for access token via backend
        const response = await fetch(API_URL + '/api/channels/' + channelType + '/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + authToken
          },
          body: JSON.stringify({ code: code })
        });

        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response data:', data);
        
        // Log debug info if present
        if (data.debug) {
          console.log('=== DEBUG INFO FROM BACKEND ===');
          console.log('Step:', data.debug.step);
          console.log('Status Code:', data.debug.status_code);
          console.log('Raw Response:', data.debug.raw_response);
          console.log('Me Response:', data.debug.me_response);
          console.log('Token (first 30):', data.debug.token_first_30);
          console.log('App ID Used:', data.debug.app_id_used);
          console.log('Redirect URI Used:', data.debug.redirect_uri_used);
          console.log('================================');
        }

        if (data.success) {
          // Case A: Success
          setStatus('success');
          setMessage('Successfully connected!');
          if (data.instagram_username) {
            setSubMessage('@' + data.instagram_username);
          }
          setTimeout(() => {
            navigate('/channels?oauth_success=true&username=' + (data.instagram_username || ''));
          }, 1500);
        } else if (data.case === 'B') {
          // Case B: No Instagram Business Account
          setStatus('warning');
          setMessage('No Instagram Business Account Found');
          setSubMessage(data.instruction || 'Please link your Instagram to a Facebook Page.');
          setTimeout(() => {
            navigate('/channels?oauth_error=no_instagram&case=B&message=' + encodeURIComponent(data.message || ''));
          }, 3000);
        } else if (data.case === 'C') {
          // Case C: No Facebook Pages
          setStatus('warning');
          setMessage('No Facebook Pages Found');
          // Show debug info in console
          if (data.debug) {
            console.error('CASE C Debug - Raw /me/accounts response:', data.debug.raw_response);
          }
          setSubMessage('You need admin access to at least one Facebook Page.');
          setTimeout(() => {
            navigate('/channels?oauth_error=no_pages&case=C&message=' + encodeURIComponent(data.message || ''));
          }, 3000);
        } else {
          // Generic error
          setStatus('error');
          setMessage('Connection failed');
          setSubMessage(data.detail || data.error || 'Please try again.');
          setTimeout(() => {
            navigate('/channels?oauth_error=' + (data.error || 'connection_failed'));
          }, 2000);
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setMessage('Connection failed');
        setSubMessage('Unable to complete connection. Please try again.');
        setTimeout(() => {
          navigate('/channels?oauth_error=server_error');
        }, 2000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, token]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
        {status === 'processing' && (
          <div>
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
            <p className="text-gray-500">Please wait...</p>
          </div>
        )}
        
        {status === 'success' && (
          <div>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
            {subMessage && <p className="text-green-600 font-medium">{subMessage}</p>}
            <p className="text-sm text-gray-500 mt-4">Redirecting...</p>
          </div>
        )}
        
        {status === 'warning' && (
          <div>
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
            <p className="text-gray-600 mt-2">{subMessage}</p>
            <p className="text-sm text-gray-500 mt-4">Redirecting...</p>
          </div>
        )}
        
        {status === 'error' && (
          <div>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
            <p className="text-gray-600">{subMessage}</p>
            <p className="text-sm text-gray-500 mt-4">Redirecting...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
